import React, { useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Compass, MapPin, HelpCircle } from "lucide-react";

const CONTAINER = "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8";
const BRAND = "#1C4358";
const ACCENT = "#9C1E22";

function flatten(coords, out) {
  if (!Array.isArray(coords)) return;
  if (coords.length >= 2 && typeof coords[0] === "number" && typeof coords[1] === "number") {
    out.push([coords[1], coords[0]]); // GeoJSON is [lng, lat] -> Leaflet [lat, lng]
    return;
  }
  coords.forEach((c) => flatten(c, out));
}

function geoPositions(geojson) {
  const out = [];
  const walk = (g) => {
    if (!g || typeof g !== "object") return;
    if (g.type === "FeatureCollection") (g.features || []).forEach(walk);
    else if (g.type === "Feature") walk(g.geometry);
    else flatten(g.coordinates, out);
  };
  walk(geojson);
  return out;
}

export function ServiceAreaMap({ coverage, label, onCta }) {
  const { geojson, center, mapped_area: mappedArea, areas = [] } = coverage || {};

  const bounds = useMemo(() => {
    if (!geojson) return null;
    const pts = geoPositions(geojson);
    if (pts.length < 2) return null;
    const lats = pts.map((p) => p[0]);
    const lngs = pts.map((p) => p[1]);
    return [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]];
  }, [geojson]);

  const hasMap = !!geojson && (!!bounds || (Array.isArray(center) && center.length === 2));

  return (
    <section className="py-14 sm:py-20 bg-[#F4F8FA]" data-testid="service-area-section">
      <div className={CONTAINER}>
        <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]">
          <Compass size={14} className="inline mr-1" /> Where we help
        </p>
        <h2 className="mt-2 text-2xl sm:text-3xl font-display font-bold text-[#1C4358]">
          Areas we serve in {label}
        </h2>
        {mappedArea && (
          <p className="mt-3 max-w-2xl text-[16px] text-[#4B5563] leading-relaxed" data-testid="service-area-blurb">
            {mappedArea}
          </p>
        )}

        <div className="mt-8 grid gap-6 lg:grid-cols-5">
          <div
            className="lg:col-span-3 rounded-2xl overflow-hidden border border-[#1C4358]/12 shadow-sm bg-white"
            data-testid="service-area-map"
          >
            {hasMap ? (
              <div style={{ height: 380, width: "100%" }} data-testid="service-area-map-canvas">
                <MapContainer
                  {...(bounds
                    ? { bounds, boundsOptions: { padding: [24, 24] } }
                    : { center, zoom: 10 })}
                  scrollWheelZoom={false}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <GeoJSON
                    data={geojson}
                    style={{ color: BRAND, weight: 2, fillColor: ACCENT, fillOpacity: 0.12 }}
                  />
                </MapContainer>
              </div>
            ) : (
              <div
                className="h-[380px] flex flex-col items-center justify-center text-center px-6"
                data-testid="service-area-map-fallback"
              >
                <MapPin size={34} className="text-[#9BB9CE]" />
                <p className="mt-3 text-[15px] text-[#4B5563] max-w-sm">
                  We serve {label} and the surrounding area. See the communities we cover.
                </p>
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            {areas.length > 0 && (
              <div className="rounded-2xl bg-white border border-[#1C4358]/12 p-5" data-testid="service-area-list">
                <h3 className="font-display font-semibold text-[#1C4358] text-[16px]">Communities we cover</h3>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                  {areas.map((a, i) => (
                    <li
                      key={i}
                      data-testid={`service-area-item-${i}`}
                      className="flex items-center gap-1.5 text-[14.5px] text-[#4B5563]"
                    >
                      <MapPin size={13} className="text-[#9C1E22] shrink-0" /> {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-4 rounded-2xl bg-[#1C4358] text-white p-5" data-testid="service-area-cta">
              <p className="font-display font-semibold text-[16px] inline-flex items-center gap-2">
                <HelpCircle size={17} /> Not sure if you're covered?
              </p>
              <p className="mt-1.5 text-[14px] text-white/85">
                Tell us where your loved one lives and we'll confirm your local team in minutes.
              </p>
              <button
                type="button"
                onClick={onCta}
                data-testid="service-area-cta-btn"
                className="mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white font-semibold text-[14px] transition-colors"
              >
                Check my area
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
